import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Layout } from "@/components/Layout";
import api from "@/lib/api";
import { Upload, FileText, Trash2, ArrowLeft, Pencil, GitCompareArrows, History, Mail, Sparkles, FilePlus, FileDown } from "lucide-react";
import { format, formatDistanceToNow } from "date-fns";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

export default function PatientDetail() {
  const { patientId } = useParams();
  const navigate = useNavigate();
  const [patient, setPatient] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/patients/${patientId}`);
      setPatient(data);
    } catch {
      toast.error("Patient not found");
      navigate("/patients");
    } finally {
      setLoading(false);
    }
  }, [patientId, navigate]);

  useEffect(() => { load(); }, [load]);  // `load` is useCallback'd above with [patientId, navigate]

  const removeReport = async (reportId) => {
    if (!window.confirm("Delete this report?")) return;
    await api.delete(`/reports/${reportId}`);
    toast.success("Report deleted");
    load();
  };

  const removePatient = async () => {
    if (!window.confirm(`Delete ${patient.name} and all their reports?`)) return;
    await api.delete(`/patients/${patientId}`);
    toast.success("Patient deleted");
    navigate("/patients");
  };

  if (loading) return <Layout><div className="spinner mx-auto mt-10" /></Layout>;
  if (!patient) return null;

  return (
    <Layout>
      <Link data-testid="patient-back-link" to="/patients" className="inline-flex items-center gap-1 text-sm mb-6" style={{ color: "var(--color-text-secondary)" }}>
        <ArrowLeft className="w-3.5 h-3.5" /> All patients
      </Link>

      <div className="bg-white border rounded-lg p-8 mb-8" style={{ borderColor: "var(--color-border)" }}>
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="text-xs uppercase tracking-widest" style={{ color: "var(--color-text-secondary)" }}>Patient</div>
            <h1 className="font-display text-3xl md:text-4xl font-semibold mt-2">{patient.name}</h1>
            <div className="text-sm mt-2" style={{ color: "var(--color-text-secondary)" }}>
              {patient.age ? `${patient.age} years old` : ""}{patient.age && patient.sex ? " · " : ""}{patient.sex || ""}
              {patient.email && (
                <>
                  {(patient.age || patient.sex) && " · "}
                  <a href={`mailto:${patient.email}`} className="underline-offset-2 hover:underline" style={{ color: "var(--color-primary)" }}>{patient.email}</a>
                </>
              )}
            </div>
            {patient.notes && <p className="mt-4 text-sm max-w-prose" style={{ color: "var(--color-text-secondary)" }}>{patient.notes}</p>}
          </div>
          <div className="flex items-center gap-2">
            <EditPatientButton patient={patient} onSaved={load} />
            {(patient.reports || []).filter(r => r.status === "ready").length >= 2 && (
              <Link
                data-testid="patient-compare-button"
                to={`/patients/${patient.patient_id}/compare`}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-md border font-medium hover:bg-white"
                style={{ borderColor: "var(--color-border)" }}
              >
                <GitCompareArrows className="w-4 h-4" /> Compare
              </Link>
            )}
            <Link data-testid="patient-upload-button" to={`/upload?patient=${patient.patient_id}`} className="inline-flex items-center gap-2 px-4 py-2 rounded-md text-white font-medium" style={{ background: "var(--color-primary)" }}>
              <Upload className="w-4 h-4" /> New report
            </Link>
            <button data-testid="patient-delete-button" onClick={removePatient} className="p-2 rounded-md border hover:bg-[#F9F8F6]" style={{ borderColor: "var(--color-border)" }}>
              <Trash2 className="w-4 h-4" style={{ color: "var(--color-error)" }} />
            </button>
          </div>
        </div>
      </div>

      <h2 className="font-display text-xl font-semibold mb-4">Reports ({patient.reports?.length || 0})</h2>
      {!patient.reports?.length ? (
        <div className="bg-white border rounded-lg p-12 text-center" style={{ borderColor: "var(--color-border)" }}>
          <FileText className="w-10 h-10 mx-auto mb-3" style={{ color: "var(--color-secondary)" }} />
          <p className="font-display text-lg">No reports for this patient</p>
          <p className="text-sm mt-1" style={{ color: "var(--color-text-secondary)" }}>Upload an imaging report to generate a narrative.</p>
        </div>
      ) : (
        <div className="bg-white border rounded-lg divide-y" style={{ borderColor: "var(--color-border)" }}>
          {patient.reports.map((r) => (
            <div key={r.report_id} className="flex items-center justify-between px-6 py-4 hover:bg-[#F9F8F6]">
              <Link data-testid={`patient-report-row-${r.report_id}`} to={`/reports/${r.report_id}`} className="flex-1">
                <div className="font-medium">{r.modality} · {r.original_filename}</div>
                <div className="text-xs mt-0.5" style={{ color: "var(--color-text-secondary)" }}>
                  {format(new Date(r.created_at), "d MMM yyyy · HH:mm")}
                </div>
              </Link>
              <button data-testid={`patient-report-delete-${r.report_id}`} onClick={() => removeReport(r.report_id)} className="p-2 rounded-md hover:bg-white">
                <Trash2 className="w-4 h-4" style={{ color: "var(--color-error)" }} />
              </button>
            </div>
          ))}
        </div>
      )}

      <PatientTimeline patientId={patient.patient_id} />
    </Layout>
  );
}


function EditPatientButton({ patient, onSaved }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    name: patient.name || "",
    age: patient.age ?? "",
    sex: patient.sex || "",
    email: patient.email || "",
    notes: patient.notes || "",
  });
  const [saving, setSaving] = useState(false);

  // Re-seed when dialog opens against the latest patient values. `setForm` is a
  // useState setter (always stable) — not a real dep.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (open) {
      setForm({
        name: patient.name || "",
        age: patient.age ?? "",
        sex: patient.sex || "",
        email: patient.email || "",
        notes: patient.notes || "",
      });
    }
  }, [open, patient]);

  // Build PATCH body with ONLY fields that changed
  const dirtyChanges = () => {
    const out = {};
    if (form.name !== (patient.name || "")) out.name = form.name;
    const newAge = form.age === "" ? null : Number(form.age);
    if (newAge !== (patient.age ?? null)) out.age = newAge;
    if (form.sex !== (patient.sex || "")) out.sex = form.sex;
    if (form.email !== (patient.email || "")) out.email = form.email;
    if (form.notes !== (patient.notes || "")) out.notes = form.notes;
    return out;
  };

  const save = async (e) => {
    e.preventDefault();
    const changes = dirtyChanges();
    if (Object.keys(changes).length === 0) {
      setOpen(false);
      return;
    }
    setSaving(true);
    try {
      await api.patch(`/patients/${patient.patient_id}`, changes);
      toast.success("Patient updated");
      setOpen(false);
      await onSaved?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Update failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <button
        data-testid="patient-edit-button"
        onClick={() => setOpen(true)}
        className="inline-flex items-center gap-2 px-4 py-2 rounded-md border font-medium hover:bg-white"
        style={{ borderColor: "var(--color-border)" }}
      >
        <Pencil className="w-4 h-4" /> Edit details
      </button>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Edit patient</DialogTitle>
        </DialogHeader>
        <form onSubmit={save} className="space-y-4">
          <div>
            <label className="text-xs uppercase tracking-widest" style={{ color: "var(--color-text-secondary)" }}>Full name</label>
            <input data-testid="patient-edit-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5 w-full border bg-white rounded-md px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-[#2A4736]" style={{ borderColor: "var(--color-border)" }} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs uppercase tracking-widest" style={{ color: "var(--color-text-secondary)" }}>Age</label>
              <input data-testid="patient-edit-age" type="number" value={form.age} onChange={(e) => setForm({ ...form, age: e.target.value })} className="mt-1.5 w-full border bg-white rounded-md px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-[#2A4736]" style={{ borderColor: "var(--color-border)" }} />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest" style={{ color: "var(--color-text-secondary)" }}>Sex</label>
              <select data-testid="patient-edit-sex" value={form.sex} onChange={(e) => setForm({ ...form, sex: e.target.value })} className="mt-1.5 w-full border bg-white rounded-md px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-[#2A4736]" style={{ borderColor: "var(--color-border)" }}>
                <option value="">—</option>
                <option value="Female">Female</option>
                <option value="Male">Male</option>
                <option value="Other">Other</option>
              </select>
            </div>
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest" style={{ color: "var(--color-text-secondary)" }}>Email</label>
            <input data-testid="patient-edit-email" type="email" placeholder="patient@example.com" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1.5 w-full border bg-white rounded-md px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-[#2A4736]" style={{ borderColor: "var(--color-border)" }} />
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest" style={{ color: "var(--color-text-secondary)" }}>Notes</label>
            <textarea data-testid="patient-edit-notes" rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-1.5 w-full border bg-white rounded-md px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-[#2A4736]" style={{ borderColor: "var(--color-border)" }} />
          </div>
          <DialogFooter>
            <button data-testid="patient-edit-submit" type="submit" disabled={saving} className="px-5 py-2.5 rounded-md text-white font-medium disabled:opacity-60" style={{ background: "var(--color-primary)" }}>
              {saving ? "Saving…" : "Save changes"}
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}


const EVENT_META = {
  report_created: { label: "Report", Icon: FilePlus, color: "var(--color-primary)" },
  edit:           { label: "Edit",   Icon: Pencil,   color: "var(--color-accent)" },
  email_sent:     { label: "Email",  Icon: Mail,     color: "var(--color-info)" },
  comparison:     { label: "Compare",Icon: Sparkles, color: "var(--color-secondary)" },
};

const FILTERS = [
  { key: "all",            label: "All" },
  { key: "report_created", label: "Reports" },
  { key: "edit",           label: "Edits" },
  { key: "email_sent",     label: "Emails" },
  { key: "comparison",     label: "Comparisons" },
];

function PatientTimeline({ patientId }) {
  const [events, setEvents] = useState(null);
  const [filter, setFilter] = useState("all");
  const [error, setError] = useState(false);
  const [exporting, setExporting] = useState(false);

  // Fetch the timeline once when the patient changes. `alive`, `data` and the
  // module-scope `api` import aren't React deps — `alive` is a local mutable
  // flag, `data` is a destructured local, `api` is a stable import.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get(`/patients/${patientId}/timeline`);
        if (alive) setEvents(data.events || []);
      } catch {
        if (alive) setError(true);
      }
    })();
    return () => { alive = false; };
  }, [patientId]);

  const exportPdf = async () => {
    setExporting(true);
    try {
      const res = await api.get(`/patients/${patientId}/timeline.pdf`, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const dispo = res.headers?.["content-disposition"] || "";
      const match = /filename="?([^"]+)"?/i.exec(dispo);
      const filename = match?.[1] || `${patientId}_activity_audit.pdf`;
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Audit PDF downloaded");
    } catch {
      toast.error("Couldn't generate PDF");
    } finally {
      setExporting(false);
    }
  };

  const filtered = useMemo(() => {
    if (!events) return [];
    if (filter === "all") return events;
    // `e` is the lambda parameter of .filter(), not a hook dep.
    return events.filter((e) => e.type === filter);
  }, [events, filter]);

  const counts = useMemo(() => {
    const c = { all: events?.length || 0, report_created: 0, edit: 0, email_sent: 0, comparison: 0 };
    // `c`, `e`, and `.length` below are all local — not hook deps.
    (events || []).forEach((e) => { if (c[e.type] != null) c[e.type] += 1; });
    return c;
  }, [events]);

  return (
    <section className="mt-12" data-testid="patient-timeline-section">
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <History className="w-5 h-5" style={{ color: "var(--color-text-secondary)" }} />
        <h2 className="font-display text-xl font-semibold">Activity timeline</h2>
        {events && (
          <span className="text-xs px-2 py-0.5 rounded-full" style={{ background: "var(--bg-canvas)", color: "var(--color-text-secondary)" }}>
            {counts.all} event{counts.all === 1 ? "" : "s"}
          </span>
        )}
        <div className="ml-auto">
          <button
            data-testid="timeline-export-pdf"
            onClick={exportPdf}
            disabled={exporting || !events || events.length === 0}
            className="inline-flex items-center gap-2 text-sm px-3 py-1.5 rounded-md border font-medium hover:bg-white disabled:opacity-50 disabled:cursor-not-allowed transition"
            style={{ borderColor: "var(--color-border)", color: "var(--color-text-primary)" }}
            title="Download a medico-legal audit PDF of this timeline"
          >
            <FileDown className="w-4 h-4" />
            {exporting ? "Generating…" : "Export PDF"}
          </button>
        </div>
      </div>

      {events && events.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-4">
          {FILTERS.map((f) => {
            const active = filter === f.key;
            const count = counts[f.key];
            return (
              <button
                key={f.key}
                data-testid={`timeline-filter-${f.key}`}
                onClick={() => setFilter(f.key)}
                className="text-xs px-3 py-1.5 rounded-full border transition"
                style={{
                  borderColor: active ? "var(--color-primary)" : "var(--color-border)",
                  background: active ? "var(--color-primary)" : "var(--bg-surface)",
                  color: active ? "#fff" : "var(--color-text-primary)",
                }}
              >
                {f.label}{count != null ? ` · ${count}` : ""}
              </button>
            );
          })}
        </div>
      )}

      {error && (
        <div className="bg-white border rounded-lg p-6 text-sm" style={{ borderColor: "var(--color-border)", color: "var(--color-text-secondary)" }}>
          Couldn&apos;t load timeline.
        </div>
      )}

      {!error && events === null && (
        <div className="bg-white border rounded-lg p-8 flex justify-center" style={{ borderColor: "var(--color-border)" }}>
          <div className="spinner" />
        </div>
      )}

      {!error && events && events.length === 0 && (
        <div className="bg-white border rounded-lg p-10 text-center" style={{ borderColor: "var(--color-border)" }}>
          <History className="w-8 h-8 mx-auto mb-2" style={{ color: "var(--color-secondary)" }} />
          <p className="font-display text-base">No activity yet</p>
          <p className="text-sm mt-1" style={{ color: "var(--color-text-secondary)" }}>
            Uploads, edits, emails, and comparisons will appear here.
          </p>
        </div>
      )}

      {!error && filtered.length > 0 && (
        <ol className="relative bg-white border rounded-lg px-6 py-4" style={{ borderColor: "var(--color-border)" }}>
          <span
            className="absolute top-6 bottom-6 w-px"
            style={{ left: "2.15rem", background: "var(--color-border)" }}
            aria-hidden
          />
          {filtered.map((e, idx) => (
            <TimelineRow key={`${e.type}-${idx}-${e.at}`} event={e} />
          ))}
        </ol>
      )}

      {!error && events && events.length > 0 && filtered.length === 0 && (
        <div className="bg-white border rounded-lg p-8 text-center text-sm" style={{ borderColor: "var(--color-border)", color: "var(--color-text-secondary)" }}>
          No events in this category.
        </div>
      )}
    </section>
  );
}

function TimelineRow({ event }) {
  const meta = EVENT_META[event.type] || EVENT_META.edit;
  const Icon = meta.Icon;

  const when = event.at ? new Date(event.at) : null;
  const whenAbs = when ? format(when, "d MMM yyyy · HH:mm") : "—";
  const whenRel = when ? formatDistanceToNow(when, { addSuffix: true }) : "";

  const { title, detail, to } = describeEvent(event);

  const Content = (
    <div className="flex-1 min-w-0">
      <div className="flex flex-wrap items-baseline gap-x-2">
        <span className="text-xs uppercase tracking-widest" style={{ color: meta.color }}>{meta.label}</span>
        <span className="font-medium truncate">{title}</span>
      </div>
      {detail && (
        <p className="text-sm mt-0.5" style={{ color: "var(--color-text-secondary)" }}>
          {detail}
        </p>
      )}
      <div className="text-xs mt-1" style={{ color: "var(--color-text-secondary)" }}>
        {whenAbs}{whenRel && <span className="opacity-70"> · {whenRel}</span>}
      </div>
    </div>
  );

  return (
    <li className="relative flex items-start gap-4 py-4 first:pt-2 last:pb-2" data-testid={`timeline-event-${event.type}`}>
      <div
        className="relative z-10 flex items-center justify-center w-9 h-9 rounded-full border shrink-0"
        style={{ borderColor: "var(--color-border)", background: "var(--bg-surface)" }}
      >
        <Icon className="w-4 h-4" style={{ color: meta.color }} />
      </div>
      {to ? (
        <Link to={to} className="flex-1 min-w-0 hover:opacity-90">
          {Content}
        </Link>
      ) : Content}
    </li>
  );
}

function describeEvent(e) {
  switch (e.type) {
    case "report_created":
      return {
        title: `${e.modality || "Report"} uploaded`,
        detail: e.original_filename || null,
        to: e.report_id ? `/reports/${e.report_id}` : null,
      };
    case "edit": {
      const scopeBits = [];
      if (e.language) scopeBits.push(e.language.toUpperCase());
      if (e.revert_scope) scopeBits.push(`scope: ${e.revert_scope}`);
      const detail = [e.note, scopeBits.join(" · ")].filter(Boolean).join(" — ");
      return {
        title: `Narrative edited by ${e.by || "Clinician"}`,
        detail: detail || null,
        to: e.report_id ? `/reports/${e.report_id}` : null,
      };
    }
    case "email_sent":
      return {
        title: `Sent to ${e.email || "patient"}`,
        detail: e.language ? `Language: ${e.language.toUpperCase()}` : null,
        to: e.report_id ? `/reports/${e.report_id}` : null,
      };
    case "comparison":
      return {
        title: "Longitudinal comparison generated",
        detail: e.baseline_id && e.follow_up_id ? `${e.baseline_id.slice(0, 10)}… ↔ ${e.follow_up_id.slice(0, 10)}…` : null,
        to: e.follow_up_id && e.baseline_id ? `/reports/${e.follow_up_id}` : null,
      };
    default:
      return { title: "Event", detail: null, to: null };
  }
}
